import { widgetScriptLocation } from 'constants/dom';
import { defaultWidgetMargin, defaultZIndex } from 'constants/general';
import React from 'react';
import ReactDOM from 'react-dom';
import { DOMService } from 'services/DOMService';
import messageListStyles from 'src/components/Chat/ChatContent/MessageList/MessageList.less';
import chatStyles from 'src/components/Chat/ChatRoot/Chat.less';
import { WidgetStore } from 'state/widgetStore';
import styles from 'styles/widget.less';
import { ChatStateController } from 'utils/chatState';
import { generateSelectorClassName, loadStyleSheet } from 'utils/dom';
import { ThemeMode, watchExternalThemeChange } from 'utils/themes';

import { ServiceConfiguration } from '../ServiceConfiguration';

export class DOMServiceImpl implements DOMService {
    private customRootQuery: string;
    private overrideTheme: ThemeMode;
    private _isUsingCustomRoot = false;
    private widgetElement: React.ReactElement;
    private widgetStore: WidgetStore;
    private unwatchThemeChange: () => void;

    public widgetRoot: HTMLElement | null = null;
    public widgetStyleSheets: Map<string, HTMLStyleElement | null> = new Map();

    get widgetStyleSheetsAsArray() {
        return Array.from(this.widgetStyleSheets.values());
    }

    chat: HTMLDivElement | null;
    chatContent: HTMLDivElement | null;
    chatContainer: HTMLDivElement | null;

    private awaitChatContent() {
        const awaitInterval = setInterval(() => {
            const chat: HTMLDivElement = this.widgetRoot?.querySelector(
                generateSelectorClassName(chatStyles.chat),
            );

            const chatContainer: HTMLDivElement = this.widgetRoot?.querySelector(
                generateSelectorClassName(chatStyles.chatContainer),
            );

            const chatContent: HTMLDivElement = this.widgetRoot?.querySelector(
                generateSelectorClassName(messageListStyles.messageList),
            );

            if (chat && chatContent && chatContainer) {
                this.chat = chat;
                this.chatContainer = chatContainer;
                this.chatContent = chatContent;

                clearInterval(awaitInterval);
            }
        }, 250);
    }

    configure({ widgetParameters, widgetElement, widgetStore }: ServiceConfiguration): void {
        this.customRootQuery = widgetParameters?.displaySettings?.customRootQuery;
        this.overrideTheme = widgetParameters?.displaySettings?.overrideTheme;
        this.widgetElement = widgetElement;
        this.widgetStore = widgetStore;
        this.widgetStyleSheets.clear();

        this.listenDOMParameters();
        this.awaitChatContent();
    }

    private async _loadFontsStyleSheet() {
        if (this.widgetStyleSheets.has('fonts')) {
            return;
        }

        let persist = true;
        try {
            this.widgetStyleSheets.set('fonts', null);
            await document.fonts.ready;

            for (const font of document.fonts) {
                const fontFamily = font.family?.replace(/['"]+/g, '');
                if (fontFamily === 'VTBGroupUI') {
                    persist = false;
                    break;
                }
            }
        } catch {
            this.widgetStyleSheets.delete('fonts');
        }

        if (!persist) {
            this.widgetStyleSheets.set('fonts', null);
            throw new Error(`Fonts are already uploaded`);
        }

        try {
            this.widgetStyleSheets.set('fonts', null);
            const styleSheet = await loadStyleSheet(
                `${widgetScriptLocation}/static/fonts.css?v=${BUILD_VERSION}`,
            );
            this.widgetStyleSheets.set('fonts', styleSheet);
        } catch {
            this.widgetStyleSheets.delete('fonts');
        }
    }

    private async _loadWidgetStyleSheet() {
        if (this.widgetStyleSheets.has('widget')) {
            return;
        }

        try {
            this.widgetStyleSheets.set('widget', null);
            const styleSheet = await loadStyleSheet(
                `${widgetScriptLocation}/widget.css?v=${BUILD_VERSION}`,
            );
            this.widgetStyleSheets.set('widget', styleSheet);
        } catch {
            this.widgetStyleSheets.delete('widget');
        }

        if (this.widgetRoot) {
            this.widgetRoot.style.removeProperty('display');
        }
    }

    private _freeWidgetStyleSheets() {
        if (this.widgetStyleSheets.size) {
            const keys = Array.from(this.widgetStyleSheets.keys());
            keys.forEach((key) => {
                const styleSheet = this.widgetStyleSheets.get(key);
                if (styleSheet) {
                    styleSheet?.remove();
                    this.widgetStyleSheets.delete(key);
                }
            });
        }
    }

    public inject(loadFonts = true) {
        let widgetRoot: HTMLElement;

        if (this.customRootQuery) {
            widgetRoot = document.querySelector(this.customRootQuery) as HTMLElement;

            if (!widgetRoot) {
                throw `Failed to query custom widget root by selector '${this.customRootQuery}'`;
            }

            widgetRoot.classList.add(styles.widgetRoot_custom);
            this._isUsingCustomRoot = true;
        } else {
            widgetRoot = document.createElement('div');
            widgetRoot.style.padding = this.getWidgetMarginParameters();

            widgetRoot.addEventListener('keyup', (e) => {
                // В макротаске, т.к. этот callback срабатывал раньше других при проверке на кнопку Escape
                // TODO в проект нужно добавить FocusTrap
                setTimeout(() => {
                    if (
                        e.key === 'Escape' &&
                        !e.defaultPrevented &&
                        this.widgetStore.getState().chat.open
                    ) {
                        ChatStateController.toggleChat(false);
                    }
                }, 100);
            });

            document.body.appendChild(widgetRoot);
        }

        widgetRoot.classList.add(styles.widgetRoot);

        this.widgetRoot = widgetRoot;
        widgetRoot.toggleAttribute('data-widget-root', true);
        widgetRoot.style.setProperty('display', 'none');

        Promise.allSettled(
            [loadFonts && this._loadFontsStyleSheet(), this._loadWidgetStyleSheet()].filter(
                Boolean,
            ),
        );

        this.unwatchThemeChange = watchExternalThemeChange(() => widgetRoot, this.overrideTheme);

        this.widgetRoot = widgetRoot;

        // eslint-disable-next-line react/no-deprecated
        ReactDOM.render(this.widgetElement, this.widgetRoot);
    }

    public cleanup() {
        // eslint-disable-next-line react/no-deprecated
        ReactDOM.unmountComponentAtNode(this.widgetRoot);

        this._freeWidgetStyleSheets();
        this.unwatchThemeChange?.();

        if (this.isUsingCustomRoot) {
            this.widgetRoot.classList.remove(styles.widgetRoot, styles.widgetRoot_custom);
            this.widgetRoot.removeAttribute('data-widget-root');
        } else {
            this.widgetRoot.remove();
        }

        this.widgetRoot = null;
        this._isUsingCustomRoot = false;
    }

    public focusTo(selectors: string) {
        const element = this.widgetRoot?.querySelector(selectors) as HTMLElement;
        element?.focus();
    }

    public get isUsingCustomRoot() {
        return this._isUsingCustomRoot;
    }

    private getDisplayParameters() {
        return this.widgetStore.getState().parameters?.displaySettings ?? {};
    }

    private getWidgetMarginParameters() {
        const { marginTop, marginRight, marginBottom, marginLeft } = this.getDisplayParameters();

        const mt = `${marginTop ?? defaultWidgetMargin.top}px`;
        const mr = `${marginRight ?? defaultWidgetMargin.right}px`;
        const mb = `${marginBottom ?? defaultWidgetMargin.bottom}px`;
        const ml = `${marginLeft ?? defaultWidgetMargin.left}px`;

        return `${mt} ${mr} ${mb} ${ml}`;
    }

    private getWidgetZIndexParameter() {
        const { zIndex } = this.getDisplayParameters();
        return String(zIndex ?? defaultZIndex);
    }

    private listenDOMParameters() {
        const store = this.widgetStore;
        store.subscribe(() => {
            const state = store.getState();
            const { customRootQuery, overrideTheme } = state.parameters?.displaySettings ?? {};

            if (customRootQuery !== this.customRootQuery || overrideTheme !== this.overrideTheme) {
                this.customRootQuery = customRootQuery;
                this.overrideTheme = overrideTheme;
                this.onDOMParametersChanged();
            }

            if (!this.isUsingCustomRoot) {
                const root = this.widgetRoot;
                if (root) {
                    root.style.padding = this.getWidgetMarginParameters();

                    const zIndex = this.getWidgetZIndexParameter();
                    if (root.style.zIndex !== zIndex) root.style.zIndex = zIndex;
                }
            }
        });
    }

    private onDOMParametersChanged() {
        this.cleanup();
        this.inject();
    }
}
